import { classNames } from '@dimjs/utils';
import { useRef } from 'react';
import { useEffectCustom } from '../hooks/use-effect-custom';
import { attachPropertiesToComponent } from '../utils/inner-use/attach-properties-to-component';
import { FormContext } from './context';
import { Input } from './form-input/input';
import { FormModal } from './form-input/modal';
import { FormText } from './form-input/text';
import { Textarea } from './form-input/textarea';
import { VirtualInput } from './form-input/virtual-input';
import { FormItem } from './form-item';
import { FormStore } from './helps/form-store';
import { useForm } from './helps/use-form';
import { useFormInstance } from './helps/use-form-instance';
import './style.less';
import { FormProps } from './types';

export type FormStaticMethods = {
  Input: typeof Input;
  Item: typeof FormItem;
  Textarea: typeof Textarea;
  useForm: typeof useForm;
  VirtualInput: typeof VirtualInput;
  /**
   * 自定义FormItem，可用于跳转页面取值交互
   * ```
   * 比普通自定义FormItem，多了一个clickTigger属性，用于触发click事件通知
   * ```
   */
  Text: typeof FormText;
  /**
   * 自定义FormItem，用于非Picker类的弹框结构
   * 1. 如果为Picker选择器类型的，使用Form.Picker最合适
   * 2. 用于非Picker类的弹框结构
   */
  Modal: typeof FormModal;
};

const FormInner = (props: FormProps) => {
  const [formInstance] = useForm(props.form);
  const formInstanceInner = (formInstance._this as FormStore).getFormInner();
  const formRef = useRef<HTMLDivElement>(null);
  useEffectCustom(() => {
    if (props.initialValues) {
      formInstanceInner.setInitialValues(props.initialValues);
      formInstanceInner.setFieldsValue(props.initialValues);
    }
  }, []);

  return (
    <div className={classNames('v-form', props.className)} style={props.style} ref={formRef}>
      <FormContext.Provider
        value={{
          form: formInstanceInner,
          onValuesChange: props.onValuesChange,
          formRef,
          showErrorMessage: props.showErrorMessage,
          scrollIntoView: props.scrollIntoView,
        }}
      >
        {props.children}
      </FormContext.Provider>
    </div>
  );
};

/**
 * Form组件
 * ```
 * 1. disabled只有设置在Form.item才能起到作用
 * ```
 */
export const Form = attachPropertiesToComponent(FormInner, {
  useForm: useForm,
  useFormInstance: useFormInstance,
  Item: FormItem,
  Input: Input,
  Textarea: Textarea,
  /**
   * 虚拟键盘
   * ```
   *  1. 内置键盘包括：纯数字键盘、金额键盘、身份证键盘
   *  2. 可自定义键盘键值，例如自定义车牌省份键盘
   * ```
   */
  VirtualInput: VirtualInput,
  /**
   * 自定义FormItem，可用于跳转页面取值交互
   * ```
   * 1. 比普通自定义FormItem，多了一个clickTigger属性，用于触发click事件通知
   * 2. Form.Text的children props可继承FormTextItemCustomComptProps获取隐藏自定义组件属性
   * ```
   */
  Text: FormText,
  /**
   * 自定义FormItem，用于弹框结构
   * ```
   * 弹窗类结构包括两类
   * picker类
   * 1. 包括：DatePicker、DateRangePicker、ModalUpPicker、TabSelect、ModalSelector等选择器弹窗组件
   *    a. 此类组件在取值时，使用form.getFormModalPickerValue()可直接获取value值
   *    b. 赋值结构必须时 value
   * 2. 自定义弹框（设置isCustom=true）
   *    a. 自定义弹框响应数据中，不要存在label、value的定义字段
   *    b. 回填结构必须是 { label、value }结构，其中label用于页面Form.Item中展示
   * ```
   */
  Modal: FormModal,
});
